import { useRef } from 'react';
import {
  Eye, EyeSlash, Lock, LockOpen, CopySimple, Trash, ArrowUp, ArrowDown,
  CornersOut, ArrowClockwise, PencilSimple, Image as ImageIcon,
} from '@phosphor-icons/react';
import {
  ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuLabel,
  ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent, ContextMenuCheckboxItem,
} from '../ui/context-menu';
import { useSceneStore } from '../../stores/sceneStore';
import { useAssetStore } from '../../stores/assetStore';
import { getSpineManager } from '../../lib/spine-manager';
import type { Layer } from '@spine-workspace/shared';

export interface ContextHit {
  layerId: string | null;
  layerType: 'spine' | 'image' | null;
}

interface Props {
  hit: ContextHit | null;
  layers: Layer[];
  onFitView?: () => void;
}

// ---- Empty area menu ----------------------------------------

function EmptyAreaMenu({ onFitView }: { onFitView?: () => void }) {
  const { selectLayers, layers } = useSceneStore();
  return (
    <ContextMenuContent data-testid="canvas-ctx-menu-empty">
      <ContextMenuLabel>Canvas</ContextMenuLabel>
      <ContextMenuSeparator />
      <ContextMenuItem onClick={() => selectLayers(layers.map((l) => l.id))}>
        Select all layers
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onClick={onFitView}>
        Fit view
        <span className="ml-auto text-text-tertiary text-xs pl-4">F</span>
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

// ---- Shared layer actions (used by both spine and image menus) ----

function useLayerActions(layer: Layer) {
  const {
    toggleLayerVisibility, toggleLayerLock, duplicateLayer, removeLayer,
    moveLayerUp, moveLayerDown, updateLayerTransform, pushUndo,
  } = useSceneStore();

  function handleRename() {
    const el = document.querySelector<HTMLElement>(`[data-rename-layer="${layer.id}"]`);
    el?.dispatchEvent(new Event('start-rename', { bubbles: true }));
  }

  function handleToggleVisibility() {
    toggleLayerVisibility(layer.id);
    getSpineManager().setLayerVisible(layer.id, !layer.visible);
  }

  function handleResetTransform() {
    pushUndo();
    updateLayerTransform(layer.id, { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 });
  }

  return { handleRename, handleToggleVisibility, handleResetTransform, toggleLayerLock, duplicateLayer, removeLayer, moveLayerUp, moveLayerDown, pushUndo };
}

// ---- Spine layer menu ----------------------------------------

function SpineLayerMenu({ layer }: { layer: Layer }) {
  const {
    handleRename, handleToggleVisibility, handleResetTransform,
    toggleLayerLock, duplicateLayer, removeLayer, moveLayerUp, moveLayerDown, pushUndo,
  } = useLayerActions(layer);

  const { setLayerSkin, setLayerAnimation } = useSceneStore();
  const assets = useAssetStore((s) => s.assets);
  const asset = assets.find((a) => a.id === layer.assetId);
  const skins = asset?.metadata?.skins ?? [];
  const animations = asset?.metadata?.animations ?? [];
  const currentAnim = layer.animation.tracks[0]?.name ?? null;

  function handleSetSkin(skin: string) {
    setLayerSkin(layer.id, skin);
    getSpineManager().setSkin(layer.id, skin);
  }

  function handleSetAnimation(animName: string) {
    setLayerAnimation(layer.id, 0, animName, true);
    getSpineManager().setAnimation(layer.id, 0, animName, true);
  }

  return (
    <ContextMenuContent data-testid={`canvas-ctx-menu-${layer.id}`}>
      <ContextMenuLabel>
        <span className="text-text-tertiary text-[10px] font-normal mr-1.5">Spine</span>
        {layer.name}
      </ContextMenuLabel>
      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => getSpineManager().focusLayer(layer.id)}>
        <CornersOut size={13} className="text-text-tertiary" />
        Focus in viewport
      </ContextMenuItem>
      <ContextMenuItem onClick={handleRename}>
        <PencilSimple size={13} className="text-text-tertiary" />
        Rename
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => { pushUndo(); moveLayerUp(layer.id); }}>
        <ArrowUp size={13} className="text-text-tertiary" />
        Move up
      </ContextMenuItem>
      <ContextMenuItem onClick={() => { pushUndo(); moveLayerDown(layer.id); }}>
        <ArrowDown size={13} className="text-text-tertiary" />
        Move down
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={handleToggleVisibility}>
        {layer.visible
          ? <><EyeSlash size={13} className="text-text-tertiary" />Hide</>
          : <><Eye size={13} className="text-text-tertiary" />Show</>}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => toggleLayerLock(layer.id)}>
        {layer.locked
          ? <><LockOpen size={13} className="text-text-tertiary" />Unlock</>
          : <><Lock size={13} className="text-text-tertiary" />Lock</>}
      </ContextMenuItem>

      {/* Skin submenu */}
      {skins.length > 1 && (
        <>
          <ContextMenuSeparator />
          <ContextMenuSub>
            <ContextMenuSubTrigger>Skin: {layer.skin || 'default'}</ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {skins.map((skin) => (
                <ContextMenuCheckboxItem
                  key={skin}
                  checked={layer.skin === skin || (!layer.skin && skin === 'default')}
                  onCheckedChange={() => handleSetSkin(skin)}
                >
                  {skin}
                </ContextMenuCheckboxItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        </>
      )}

      {/* Animation submenu */}
      {animations.length > 0 && (
        <ContextMenuSub>
          <ContextMenuSubTrigger>Animation: {currentAnim ?? '—'}</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {animations.map((anim) => (
              <ContextMenuCheckboxItem
                key={anim}
                checked={currentAnim === anim}
                onCheckedChange={() => handleSetAnimation(anim)}
              >
                {anim}
              </ContextMenuCheckboxItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
      )}

      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => duplicateLayer(layer.id)}>
        <CopySimple size={13} className="text-text-tertiary" />
        Duplicate
      </ContextMenuItem>
      <ContextMenuItem
        onClick={() => { pushUndo(); removeLayer(layer.id); }}
        className="text-error hover:text-error focus:text-error"
      >
        <Trash size={13} />
        Delete
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={handleResetTransform}>
        <ArrowClockwise size={13} className="text-text-tertiary" />
        Reset transform
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

// ---- Image layer menu ----------------------------------------

function ImageLayerMenu({ layer }: { layer: Layer }) {
  const {
    handleRename, handleToggleVisibility, handleResetTransform,
    toggleLayerLock, duplicateLayer, removeLayer, moveLayerUp, moveLayerDown, pushUndo,
  } = useLayerActions(layer);

  const fileRef = useRef<HTMLInputElement>(null);
  const uploadImageAsset = useAssetStore((s) => s.uploadImageAsset);
  const updateImageLayer = useSceneStore((s) => s.updateImageLayer);

  async function handleReplaceImage(file: File) {
    try {
      const newAsset = await uploadImageAsset(file);
      const mgr = getSpineManager();
      mgr.removeImageFromScene(layer.id);
      updateImageLayer(layer.id, newAsset.id, newAsset.jsonUrl ?? '');
    } catch (err) {
      console.error('[CanvasContextMenu] Replace image failed', err);
    }
  }

  return (
    <ContextMenuContent data-testid={`canvas-ctx-menu-${layer.id}`}>
      <ContextMenuLabel>
        <span className="text-text-tertiary text-[10px] font-normal mr-1.5">Image</span>
        {layer.name}
      </ContextMenuLabel>
      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => getSpineManager().focusLayer(layer.id)}>
        <CornersOut size={13} className="text-text-tertiary" />
        Focus in viewport
      </ContextMenuItem>
      <ContextMenuItem onClick={handleRename}>
        <PencilSimple size={13} className="text-text-tertiary" />
        Rename
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => { pushUndo(); moveLayerUp(layer.id); }}>
        <ArrowUp size={13} className="text-text-tertiary" />
        Move up
      </ContextMenuItem>
      <ContextMenuItem onClick={() => { pushUndo(); moveLayerDown(layer.id); }}>
        <ArrowDown size={13} className="text-text-tertiary" />
        Move down
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={handleToggleVisibility}>
        {layer.visible
          ? <><EyeSlash size={13} className="text-text-tertiary" />Hide</>
          : <><Eye size={13} className="text-text-tertiary" />Show</>}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => toggleLayerLock(layer.id)}>
        {layer.locked
          ? <><LockOpen size={13} className="text-text-tertiary" />Unlock</>
          : <><Lock size={13} className="text-text-tertiary" />Lock</>}
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => fileRef.current?.click()}>
        <ImageIcon size={13} className="text-text-tertiary" />
        Replace image
      </ContextMenuItem>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleReplaceImage(file);
          e.target.value = '';
        }}
      />

      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => duplicateLayer(layer.id)}>
        <CopySimple size={13} className="text-text-tertiary" />
        Duplicate
      </ContextMenuItem>
      <ContextMenuItem
        onClick={() => { pushUndo(); removeLayer(layer.id); }}
        className="text-error hover:text-error focus:text-error"
      >
        <Trash size={13} />
        Delete
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={handleResetTransform}>
        <ArrowClockwise size={13} className="text-text-tertiary" />
        Reset transform
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

// ---- Multi-select menu --------------------------------------

function MultiLayerMenu({ ids, layers }: { ids: string[]; layers: Layer[] }) {
  const {
    toggleLayerVisibility, toggleLayerLock, duplicateLayer, removeLayer,
    pushUndo, updateLayerTransform,
  } = useSceneStore();

  const selected = layers.filter((l) => ids.includes(l.id));
  const allVisible = selected.every((l) => l.visible);
  const allLocked = selected.every((l) => l.locked);

  function handleToggleVisibility() {
    const next = !allVisible;
    selected.forEach((l) => {
      if (l.visible !== next) {
        toggleLayerVisibility(l.id);
        getSpineManager().setLayerVisible(l.id, next);
      }
    });
  }

  function handleToggleLock() {
    const next = !allLocked;
    selected.forEach((l) => { if (l.locked !== next) toggleLayerLock(l.id); });
  }

  function handleDelete() {
    pushUndo();
    ids.forEach((id) => removeLayer(id));
  }

  function handleResetAll() {
    pushUndo();
    ids.forEach((id) => updateLayerTransform(id, { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 }));
  }

  return (
    <ContextMenuContent data-testid="canvas-ctx-menu-multi">
      <ContextMenuLabel>{ids.length} layers selected</ContextMenuLabel>
      <ContextMenuSeparator />

      <ContextMenuItem onClick={handleToggleVisibility}>
        {allVisible
          ? <><EyeSlash size={13} className="text-text-tertiary" />Hide all</>
          : <><Eye size={13} className="text-text-tertiary" />Show all</>}
      </ContextMenuItem>
      <ContextMenuItem onClick={handleToggleLock}>
        {allLocked
          ? <><LockOpen size={13} className="text-text-tertiary" />Unlock all</>
          : <><Lock size={13} className="text-text-tertiary" />Lock all</>}
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => ids.forEach((id) => duplicateLayer(id))}>
        <CopySimple size={13} className="text-text-tertiary" />
        Duplicate all
      </ContextMenuItem>
      <ContextMenuItem
        onClick={handleDelete}
        className="text-error hover:text-error focus:text-error"
      >
        <Trash size={13} />
        Delete all
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={handleResetAll}>
        <ArrowClockwise size={13} className="text-text-tertiary" />
        Reset all transforms
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

// ---- Root export --------------------------------------------

export default function CanvasContextMenuContent({ hit, layers, onFitView }: Props) {
  const selectedLayerIds = useSceneStore((s) => s.selectedLayerIds);

  // Multi-select: right-clicked layer is inside the active multi-selection
  if (hit?.layerId && selectedLayerIds.length > 1 && selectedLayerIds.includes(hit.layerId)) {
    return <MultiLayerMenu ids={selectedLayerIds} layers={layers} />;
  }

  // Single layer hit — route to type-specific menu
  if (hit?.layerId) {
    const layer = layers.find((l) => l.id === hit.layerId);
    if (layer) {
      return layer.layerType === 'image'
        ? <ImageLayerMenu layer={layer} />
        : <SpineLayerMenu layer={layer} />;
    }
  }

  // Empty area (no hit)
  return <EmptyAreaMenu onFitView={onFitView} />;
}
